import type { Subscriber as SvelteSubscriber } from "svelte/store";

/**
 * Subscriber to a subscription
 */
type Subscriber<T> = SvelteSubscriber<T>;

/**
 * Function signature to handle new subscriptions
 */
export type SubscribeHandler<T> = (newSubscriber: Subscriber<T>) => () => void;

/**
 * Callback to provide value to a subscription
 */
type SubscriptionValueProvider<T> = () => T;

/**
 * Generic interface to provide a subscription
 */
export interface ISubscription<T> {
	subscribe: SubscribeHandler<T>,
}

/**
 * Generic subscription class to be used in services without having to
 * reimplement subscription logic.
 */
export class Subscription<T> implements ISubscription<T> {
	private subscribers: Set<Subscriber<T>> = new Set();

	private valueProvider: SubscriptionValueProvider<T>;

	constructor(valueProvider: SubscriptionValueProvider<T>) {
		this.valueProvider = valueProvider;
	}

	/**
	 * Subscribe to this subscription
	 *
	 * @returns {Function} function to unsubscribe
	 */
	subscribe(newSubscriber) {
		this.subscribers.add(newSubscriber);

		newSubscriber(this.valueProvider());

		return () => {
			this.subscribers.delete(newSubscriber);
		};
	}

	/**
	 * Notify subscribers of a new subscription value
	 */
	notify(): void {
		const value = this.valueProvider();

		for (const subscriber of this.subscribers) {
			subscriber(value);
		}
	}
}

/**
 * Extract the current value from a subscription
 */
export function getValue<T>(subscription: ISubscription<T>): T {
	let result;
	subscription.subscribe((value) => { result = value; })();
	return result;
}
